import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'

import useTranslation from '@/Hooks/UseTranslation'

import './MainSidebar.scss'

interface Props {
  menu: any[]
}

interface FlyoutProps {
  anchor: HTMLElement
  item: any
  onClose: (restoreFocus?: boolean) => void
}

interface ProjectRequiredPopoverProps {
  anchor: HTMLElement
  item: any
  onClose: (restoreFocus?: boolean) => void
}

interface SidebarItemProps {
  activeFlyoutKey?: string
  isCompact: boolean
  isDesktopCollapsed: boolean
  item: any
  itemKey: string
  activeBlockedKey?: string
  onOpenBlocked: (item: any, anchor: HTMLElement) => void
  onOpenFlyout: (item: any, anchor: HTMLElement) => void
  onToggleSubmenu: (itemKey: string) => void
  submenuOpen: boolean
}

const SIDEBAR_COLLAPSED_KEY = 'pl4-main-sidebar-collapsed'
const COMPACT_SIDEBAR_QUERY = '(max-width: 1199px)'

const getStoredCollapsedState = () => {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
  } catch (_error) {
    return false
  }
}

const getCompactSidebarState = () => (
  typeof window !== 'undefined' && window.matchMedia(COMPACT_SIDEBAR_QUERY).matches
)

const getItemKey = (item: any) => `${item.text}-${item.path}`

const useAnchoredPanelPosition = (anchor: HTMLElement) => {
  const panelRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<React.CSSProperties>({ visibility: 'hidden' })

  useLayoutEffect(() => {
    const updatePosition = () => {
      if (!panelRef.current) return

      const viewportPadding = 8
      const gap = 8
      const anchorRect = anchor.getBoundingClientRect()
      const panelRect = panelRef.current.getBoundingClientRect()
      let left = anchorRect.right + gap
      let top = anchorRect.top

      if (left + panelRect.width > window.innerWidth - viewportPadding) {
        left = Math.max(viewportPadding, anchorRect.left - panelRect.width - gap)
      }

      top = Math.max(
        viewportPadding,
        Math.min(top, window.innerHeight - panelRect.height - viewportPadding),
      )

      setPosition({ left, top })
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [anchor])

  return { panelRef, position }
}

const SidebarFlyout = ({ anchor, item, onClose }: FlyoutProps) => {
  const { panelRef, position } = useAnchoredPanelPosition(anchor)

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node

      if (!panelRef.current?.contains(target) && !anchor.contains(target)) {
        onClose()
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose(true)
    }

    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('keydown', handleKeyDown)

    const focusFrame = window.requestAnimationFrame(() => {
      panelRef.current?.querySelector<HTMLAnchorElement>('[role="menuitem"]')?.focus()
    })

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('keydown', handleKeyDown)
      window.cancelAnimationFrame(focusFrame)
    }
  }, [anchor, onClose])

  return createPortal(
    <div
      aria-label={item.text}
      className="pl4-main-sidebar-flyout"
      id="pl4-main-sidebar-flyout"
      ref={panelRef}
      role="menu"
      style={position}
    >
      <span className="pl4-main-sidebar-flyout__title">{item.text}</span>
      <ul>
        {item.submenu.map((submenuItem: any) => (
          <li key={`${submenuItem.text}-${submenuItem.path}`}>
            <Link
              aria-current={submenuItem.active ? 'page' : undefined}
              className={submenuItem.active ? 'is-active' : ''}
              onClick={() => onClose()}
              role="menuitem"
              to={submenuItem.path}
            >
              <span className={`${submenuItem.icon} pl4-main-sidebar-flyout__icon`} aria-hidden="true" />
              <span>{submenuItem.text}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>,
    document.body,
  )
}

const ProjectRequiredPopover = ({ anchor, item, onClose }: ProjectRequiredPopoverProps) => {
  const { t } = useTranslation()
  const { panelRef, position } = useAnchoredPanelPosition(anchor)

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node

      if (!panelRef.current?.contains(target) && !anchor.contains(target)) {
        onClose()
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose(true)
    }

    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('keydown', handleKeyDown)

    const focusFrame = window.requestAnimationFrame(() => {
      panelRef.current?.querySelector<HTMLAnchorElement>('.pl4-main-sidebar-popover__cta')?.focus()
    })

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('keydown', handleKeyDown)
      window.cancelAnimationFrame(focusFrame)
    }
  }, [anchor, onClose, panelRef])

  return createPortal(
    <div
      aria-labelledby="pl4-main-sidebar-popover-title"
      className="pl4-main-sidebar-popover"
      id="pl4-main-sidebar-project-required"
      ref={panelRef}
      role="dialog"
      style={position}
    >
      <strong id="pl4-main-sidebar-popover-title">{item.text}</strong>
      <p>{t('SIDEBAR:ProjectRequiredMessage')}</p>
      <Link
        className="pl4-main-sidebar-popover__cta"
        onClick={() => onClose()}
        to="/"
      >
        {t('SIDEBAR:GoToProjects')}
      </Link>
    </div>,
    document.body,
  )
}

const SidebarItem = ({
  activeBlockedKey,
  activeFlyoutKey,
  isCompact,
  isDesktopCollapsed,
  item,
  itemKey,
  onOpenBlocked,
  onOpenFlyout,
  onToggleSubmenu,
  submenuOpen,
}: SidebarItemProps) => {
  const { t } = useTranslation()
  const hasSubmenu = item.submenu?.length > 0
  const showExpandedSubmenu = hasSubmenu && !isCompact && !isDesktopCollapsed && submenuOpen
  const opensFlyout = hasSubmenu && isDesktopCollapsed
  const itemClassName = `pl4-main-sidebar__item${item.active ? ' is-active' : ''}${item.disabled ? ' is-disabled' : ''}${hasSubmenu ? ' has-submenu' : ''}`
  const content = (
    <>
      <span className="pl4-main-sidebar__icon" aria-hidden="true">
        <span className={`${item.icon}${item.active ? '_full' : ''}`} />
      </span>
      <span className="pl4-main-sidebar__label">{item.text}</span>
    </>
  )

  return (
    <li className="pl4-main-sidebar__entry">
      <div className="pl4-main-sidebar__item-row">
        {item.disabled && item.disabledReason === 'project' ? (
          <button
            aria-controls="pl4-main-sidebar-project-required"
            aria-disabled="true"
            aria-expanded={activeBlockedKey === itemKey}
            aria-haspopup="dialog"
            aria-label={item.text}
            className={itemClassName}
            onClick={(event) => onOpenBlocked(item, event.currentTarget)}
            type="button"
          >
            {content}
          </button>
        ) : item.disabled ? (
          <span
            aria-disabled="true"
            className={itemClassName}
            title={item.text}
          >
            {content}
          </span>
        ) : opensFlyout ? (
          <button
            aria-controls="pl4-main-sidebar-flyout"
            aria-expanded={activeFlyoutKey === itemKey}
            aria-haspopup="menu"
            aria-label={item.text}
            className={itemClassName}
            onClick={(event) => onOpenFlyout(item, event.currentTarget)}
            title={activeFlyoutKey === itemKey ? undefined : item.text}
            type="button"
          >
            {content}
          </button>
        ) : (
          <Link
            aria-current={item.active ? 'page' : undefined}
            className={itemClassName}
            title={item.text}
            to={item.path}
          >
            {content}
          </Link>
        )}

        {hasSubmenu && !item.disabled && !isCompact && !isDesktopCollapsed && (
          <button
            aria-expanded={submenuOpen}
            aria-label={`${t(submenuOpen ? 'SIDEBAR:CloseSubmenu' : 'SIDEBAR:OpenSubmenu')}: ${item.text}`}
            className="pl4-main-sidebar__submenu-toggle"
            onClick={() => onToggleSubmenu(itemKey)}
            type="button"
          >
            <i className={`fas fa-chevron-${submenuOpen ? 'up' : 'down'}`} aria-hidden="true" />
          </button>
        )}
      </div>

      {showExpandedSubmenu && (
        <ul aria-label={item.text} className="pl4-main-sidebar__submenu">
          {item.submenu.map((submenuItem: any) => (
            <li key={`${submenuItem.text}-${submenuItem.path}`}>
              <Link
                aria-current={submenuItem.active ? 'page' : undefined}
                className={`pl4-main-sidebar__submenu-item${submenuItem.active ? ' is-active' : ''}`}
                to={submenuItem.path}
              >
                <span className={`${submenuItem.icon} pl4-main-sidebar__submenu-icon`} aria-hidden="true" />
                <span>{submenuItem.text}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

const MainSidebar = ({ menu }: Props) => {
  const { t } = useTranslation()
  const activeParent = menu.find((item: any) => item.active && item.submenu?.length)
  const [isCollapsed, setIsCollapsed] = useState(getStoredCollapsedState)
  const [isCompact, setIsCompact] = useState(getCompactSidebarState)
  const [openSubmenuKey, setOpenSubmenuKey] = useState<string | undefined>(
    activeParent ? getItemKey(activeParent) : undefined,
  )
  const [flyoutItem, setFlyoutItem] = useState<any>()
  const [flyoutAnchor, setFlyoutAnchor] = useState<HTMLElement | null>(null)
  const [blockedItem, setBlockedItem] = useState<any>()
  const [blockedAnchor, setBlockedAnchor] = useState<HTMLElement | null>(null)
  const mainItems = menu.filter((item: any) => !item.alignRight)
  const bottomItems = menu.filter((item: any) => item.alignRight)
  const activeParentKey = activeParent ? getItemKey(activeParent) : undefined
  const activeFlyoutKey = flyoutItem ? getItemKey(flyoutItem) : undefined
  const activeBlockedKey = blockedItem ? getItemKey(blockedItem) : undefined
  const isDesktopCollapsed = isCollapsed && !isCompact

  useEffect(() => {
    const mediaQuery = window.matchMedia(COMPACT_SIDEBAR_QUERY)
    const handleChange = (event: MediaQueryListEvent) => setIsCompact(event.matches)

    setIsCompact(mediaQuery.matches)

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
    } else {
      mediaQuery.addListener(handleChange)
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange)
      } else {
        mediaQuery.removeListener(handleChange)
      }
    }
  }, [])

  useEffect(() => {
    if (activeParentKey) setOpenSubmenuKey(activeParentKey)
  }, [activeParentKey])

  useEffect(() => {
    if (isCompact || !isCollapsed) {
      setFlyoutItem(undefined)
      setFlyoutAnchor(null)
    }
  }, [isCollapsed, isCompact])

  useEffect(() => {
    if (!blockedItem) return

    const currentItem = menu.find((item: any) => getItemKey(item) === getItemKey(blockedItem))

    if (!currentItem || currentItem.disabledReason !== 'project') {
      setBlockedItem(undefined)
      setBlockedAnchor(null)
    }
  }, [blockedItem, menu])

  const closeFlyout = (restoreFocus = false) => {
    const anchor = flyoutAnchor

    setFlyoutItem(undefined)
    setFlyoutAnchor(null)

    if (restoreFocus && anchor) {
      window.requestAnimationFrame(() => anchor.focus())
    }
  }

  const closeBlockedPopover = (restoreFocus = false) => {
    const anchor = blockedAnchor

    setBlockedItem(undefined)
    setBlockedAnchor(null)

    if (restoreFocus && anchor) {
      window.requestAnimationFrame(() => anchor.focus())
    }
  }

  const handleOpenFlyout = (item: any, anchor: HTMLElement) => {
    if (activeFlyoutKey === getItemKey(item)) {
      closeFlyout(true)
      return
    }

    closeBlockedPopover()
    setFlyoutItem(item)
    setFlyoutAnchor(anchor)
  }

  const handleOpenBlocked = (item: any, anchor: HTMLElement) => {
    if (activeBlockedKey === getItemKey(item)) {
      closeBlockedPopover(true)
      return
    }

    closeFlyout()
    setBlockedItem(item)
    setBlockedAnchor(anchor)
  }

  const handleToggle = () => {
    if (isCompact) return

    closeFlyout()
    closeBlockedPopover()
    setIsCollapsed((collapsed) => {
      const nextState = !collapsed

      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(nextState))
      } catch (_error) {
        // The preference remains available for this session if storage is blocked.
      }

      return nextState
    })
  }

  const toggleLabel = isCompact
    ? t('SIDEBAR:ResizeUnavailable')
    : t(isCollapsed ? 'SIDEBAR:Expand' : 'SIDEBAR:Collapse')

  const renderItem = (item: any) => {
    const itemKey = getItemKey(item)

    return (
      <SidebarItem
        activeBlockedKey={activeBlockedKey}
        activeFlyoutKey={activeFlyoutKey}
        isCompact={isCompact}
        isDesktopCollapsed={isDesktopCollapsed}
        item={item}
        itemKey={itemKey}
        key={itemKey}
        onOpenBlocked={handleOpenBlocked}
        onOpenFlyout={handleOpenFlyout}
        onToggleSubmenu={(key) => setOpenSubmenuKey((openKey) => openKey === key ? undefined : key)}
        submenuOpen={openSubmenuKey === itemKey}
      />
    )
  }

  if (!menu.length) return null

  return (
    <>
      <aside className={`pl4-main-sidebar${isCollapsed ? ' is-collapsed' : ''}`}>
        <nav aria-label={t('SIDEBAR:MainNavigation')} className="pl4-main-sidebar__nav">
          <ul className="pl4-main-sidebar__list">
            {mainItems.map(renderItem)}
          </ul>
          <ul className="pl4-main-sidebar__list pl4-main-sidebar__list-bottom">
            {bottomItems.map(renderItem)}
          </ul>
        </nav>
        <button
          aria-label={toggleLabel}
          aria-disabled={isCompact}
          className="pl4-main-sidebar__toggle"
          disabled={isCompact}
          onClick={handleToggle}
          title={toggleLabel}
          type="button"
        >
          <i className={`fas fa-chevron-${isCollapsed ? 'right' : 'left'}`} aria-hidden="true" />
          <span className="pl4-main-sidebar__label">
            {t(isCollapsed ? 'SIDEBAR:Expand' : 'SIDEBAR:Collapse')}
          </span>
        </button>
      </aside>

      {flyoutItem && flyoutAnchor && (
        <SidebarFlyout
          anchor={flyoutAnchor}
          item={flyoutItem}
          onClose={closeFlyout}
        />
      )}

      {blockedItem && blockedAnchor && (
        <ProjectRequiredPopover
          anchor={blockedAnchor}
          item={blockedItem}
          onClose={closeBlockedPopover}
        />
      )}
    </>
  )
}

export default MainSidebar
